import { create } from 'zustand';

import { getDefaultVendorId } from '../../api/environment';
import { AuthRepository } from '../../api/repository/auth';
import type { IzteachTeacherSignInResult } from '../../api/types';
import { featureEnabled } from '../../config/features';
import { IzteachAuthRepository } from '../../api/repository/izteach-auth';
import {
  clearIzteachCredentials,
  izteachSession,
  sessionHooks,
  setIzteachCredentials,
} from '../../api/session';
import { readJson, removeKey, writeJson } from '../../api/storage';
import { useQltAuthStore } from '../qlt/auth';
import { msg, type MaybeMsg } from '../../i18n/msg.ts';
import { userError } from '../userError';
import { teacherSignInError } from './signInError';
import { ssoFailure } from './ssoError';
import type { SsoFailure } from './ssoError';

const STORAGE_KEY = 'izqlt.izteach.session';
const REFRESH_EARLY_MS = 5 * 60 * 1000;
let refreshTimer: number | undefined;

function expiryTime(value: string | number | null): number | null {
  if (value === null || value === '') return null;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function scheduleTeacherRefresh(expiry: string | number | null): void {
  if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
  refreshTimer = undefined;
  const target = expiryTime(expiry);
  if (target === null) return;
  const delay = Math.max(0, Math.min(0x7fffffff, target - Date.now() - REFRESH_EARLY_MS));
  refreshTimer = window.setTimeout(() => { void useIzteachSessionStore.getState().refreshTeacher(); }, delay);
}

interface PersistedSession {
  accessToken: string;
  refreshToken: string | null;
  userId: string | null;
  profileId: string | null;
  vendorId: string;
  roles: string[];
  accessTokenExpiredAt: string | number | null;
  kind: 'teacher' | 'student';
  /** The address the teacher signed in with; absent on sessions saved before it was kept. */
  email?: string | null;
}

// Both teacher sign-ins (password on /setup, SSO from the QLT login) persist the same session.
function teacherSession(res: IzteachTeacherSignInResult, vendorId: string, email: string | null): PersistedSession {
  const profileId = res.id || res.userId || null;
  return {
    accessToken: res.accessToken,
    refreshToken: res.refreshToken || null,
    userId: profileId,
    profileId,
    vendorId: res.vendorId || vendorId,
    roles: res.roles || ['teacher'],
    accessTokenExpiredAt: res.accessTokenExpiredAt || null,
    kind: 'teacher',
    email: res.email || email,
  };
}

export interface IzteachSessionState {
  accessToken: string | null;
  refreshToken: string | null;
  userId: string | null;
  profileId: string | null;
  vendorId: string;
  roles: string[];
  accessTokenExpiredAt: string | number | null;
  kind: 'teacher' | 'student' | null;
  email: string | null;
  warning: string | null;
  loading: boolean;
  error: MaybeMsg | null;
  /** the last SSO exchange failed; the QLT session is kept and the board shows a notice */
  ssoFailure: SsoFailure | null;
  setVendorId: (vendorId: string) => void;
  // Exchanges the current QLT jwt for IZTeach credentials (POST {student-gw}/student/qlt-signin).
  exchange: () => Promise<boolean>;
  signInTeacher: (email: string, password: string) => Promise<boolean>;
  // SSO: exchanges the current QLT jwt for an IZ teacher session (POST {teacher-gw}/teacher/qlt-signin).
  signInWithQlt: () => Promise<boolean>;
  dismissSsoFailure: () => void;
  refreshTeacher: () => Promise<boolean>;
  clear: () => void;
  hydrate: () => void;
}

export const useIzteachSessionStore = create<IzteachSessionState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  userId: null,
  profileId: null,
  vendorId: getDefaultVendorId(),
  roles: [],
  accessTokenExpiredAt: null,
  kind: null,
  email: null,
  warning: null,
  loading: false,
  error: null,
  ssoFailure: null,

  setVendorId(vendorId: string) {
    setIzteachCredentials({ vendorId });
    set({ vendorId });
  },

  async exchange() {
    const jwt = useQltAuthStore.getState().jwt;
    if (!jwt) {
      set({ error: msg('errors:session.noQlt') });
      return false;
    }
    set({ loading: true, error: null });
    try {
      const res = await AuthRepository.signInIzteachWithQlt(jwt, get().vendorId);
      if (!res || !res.accessToken) {
        set({ loading: false, error: msg('errors:session.noIzteachInfo') });
        return false;
      }
      const vendorId = res.vendorId || get().vendorId;
      setIzteachCredentials({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken || null,
        userId: res.id || null,
        vendorId,
      });
      writeJson(STORAGE_KEY, {
        accessToken: res.accessToken,
        refreshToken: res.refreshToken || null,
        userId: res.id || null,
        profileId: res.studentId || res.id || null,
        vendorId,
        roles: ['student'],
        accessTokenExpiredAt: null,
        kind: 'student',
      } as PersistedSession);
      set({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken || null,
        userId: res.id || null,
        profileId: res.studentId || res.id || null,
        vendorId,
        roles: ['student'],
        accessTokenExpiredAt: null,
        kind: 'student',
        email: null,
        warning: res.warningMsg || null,
        loading: false,
        error: null,
      });
      return true;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return false;
    }
  },

  async signInTeacher(email: string, password: string) {
    set({ loading: true, error: null });
    try {
      const res = await IzteachAuthRepository.signInTeacher(email, password, get().vendorId);
      adoptTeacherSession(teacherSession(res, get().vendorId, email));
      return true;
    } catch (error) {
      set({ loading: false, error: teacherSignInError(error, get().vendorId) });
      return false;
    }
  },

  async signInWithQlt() {
    const jwt = useQltAuthStore.getState().jwt;
    if (!jwt) return false;
    set({ ssoFailure: null });
    // the board may have signed out, or in as someone else, while the exchange was in flight
    const stale = () => useQltAuthStore.getState().jwt !== jwt;
    try {
      const res = await IzteachAuthRepository.signInTeacherWithQlt(jwt, get().vendorId);
      if (stale()) return false;
      if (!res || !res.accessToken) {
        set({ ssoFailure: { code: null, message: msg('errors:session.noIzteachSession'), needsLink: false } });
        return false;
      }
      adoptTeacherSession(teacherSession(res, get().vendorId, null));
      return true;
    } catch (error) {
      if (stale()) return false;
      set({ ssoFailure: ssoFailure(error) });
      return false;
    }
  },

  dismissSsoFailure() {
    set({ ssoFailure: null });
  },

  async refreshTeacher() {
    const state = get();
    if (state.kind !== 'teacher' || !state.profileId || !state.refreshToken) return false;
    try {
      const res = await IzteachAuthRepository.refreshTeacher(state.profileId, state.refreshToken, state.vendorId);
      if (!res.accessToken) return false;
      const next: PersistedSession = {
        accessToken: res.accessToken,
        refreshToken: res.refreshToken || state.refreshToken,
        userId: res.userId || res.id || state.userId,
        profileId: res.id || res.userId || state.profileId,
        vendorId: res.vendorId || state.vendorId,
        roles: res.roles || state.roles,
        accessTokenExpiredAt: res.accessTokenExpiredAt || null,
        kind: 'teacher',
        email: state.email,
      };
      setIzteachCredentials({ accessToken: next.accessToken, refreshToken: next.refreshToken, userId: next.userId, vendorId: next.vendorId });
      writeJson(STORAGE_KEY, next);
      set(next);
      scheduleTeacherRefresh(next.accessTokenExpiredAt);
      return true;
    } catch (error) {
      return false;
    }
  },

  clear() {
    if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
    refreshTimer = undefined;
    clearIzteachCredentials();
    removeKey(STORAGE_KEY);
    set({
      accessToken: null,
      refreshToken: null,
      userId: null,
      profileId: null,
      roles: [],
      accessTokenExpiredAt: null,
      kind: null,
      email: null,
      warning: null,
      error: null,
      ssoFailure: null,
      loading: false,
    });
  },

  hydrate() {
    if (get().accessToken) return;
    const saved = readJson<PersistedSession>(STORAGE_KEY);
    if (!saved || !saved.accessToken) return;
    setIzteachCredentials({
      accessToken: saved.accessToken,
      refreshToken: saved.refreshToken,
      userId: saved.userId,
      vendorId: saved.vendorId || izteachSession.vendorId,
    });
    set({
      accessToken: saved.accessToken,
      refreshToken: saved.refreshToken,
      userId: saved.userId,
      profileId: saved.profileId,
      vendorId: saved.vendorId || izteachSession.vendorId,
      roles: saved.roles || [],
      accessTokenExpiredAt: saved.accessTokenExpiredAt || null,
      kind: saved.kind || null,
      email: saved.email || null,
    });
    if (saved.kind === 'teacher') scheduleTeacherRefresh(saved.accessTokenExpiredAt || null);
  },
}));

function adoptTeacherSession(persisted: PersistedSession): void {
  setIzteachCredentials({
    accessToken: persisted.accessToken,
    refreshToken: persisted.refreshToken,
    userId: persisted.userId,
    vendorId: persisted.vendorId,
  });
  writeJson(STORAGE_KEY, persisted);
  useIzteachSessionStore.setState({ ...persisted, warning: null, loading: false, error: null, ssoFailure: null });
  scheduleTeacherRefresh(persisted.accessTokenExpiredAt);
}

// A 401 on an IZTeach gateway means the exchanged credentials expired - redo the exchange.
// With SSO there is no /setup to fall back on, so a failed teacher refresh re-runs the QLT exchange.
sessionHooks.reauthorizeIzteach = () => {
  const state = useIzteachSessionStore.getState();
  if (state.kind !== 'teacher') return state.exchange();
  return state.refreshTeacher().then((ok) => ok || (featureEnabled('sso') ? state.signInWithQlt() : false));
};

sessionHooks.onIzteachAuthFailure = () => {
  useIzteachSessionStore.getState().clear();
};

sessionHooks.onQltSignedOut = () => {
  useIzteachSessionStore.getState().clear();
};
